// src/pages/AdminChatsPage.jsx
import React, { useEffect, useRef, useState } from "react";

const API_BASE = "https://www.customizebychoice.com/pikchar_api/admin/chats";
const API_KEY_HEADER = "x-api-key";
const apiKeyValue = process.env.REACT_APP_ADMIN_API_KEY || "";

// ---------- Time / grouping helpers (unchanged) ----------

function getUserLastDate(user) {
  const ts =
    user.lastTimestamp || user.lastSeen || user.lastTime || user.timestamp;

  if (!ts) return null;

  // Firestore-style: { _seconds, _nanoseconds }
  if (typeof ts === "object" && ts !== null && typeof ts._seconds === "number") {
    const ms = ts._seconds * 1000 + Math.round((ts._nanoseconds || 0) / 1e6);
    return new Date(ms);
  }

  // Number: could be ms or seconds
  if (typeof ts === "number") {
    const ms = ts < 1e12 ? ts * 1000 : ts; // heuristic
    return new Date(ms);
  }

  // String date
  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

function isSameDay(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function getDateLabel(date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// users[] -> [{ dateKey, label, items: [{...user, lastDate}] }]
function groupUsersByDate(users = []) {
  const groupsByKey = {};

  users.forEach((u) => {
    const date = getUserLastDate(u);
    if (!date) return;

    const dateKey = date.toISOString().slice(0, 10); // "YYYY-MM-DD"
    const label = getDateLabel(date);

    if (!groupsByKey[dateKey]) {
      groupsByKey[dateKey] = { dateKey, label, items: [] };
    }

    groupsByKey[dateKey].items.push({
      ...u,
      lastDate: date,
    });
  });

  // object -> array, sort groups by date desc
  const groupsArr = Object.values(groupsByKey).sort(
    (a, b) => new Date(b.dateKey) - new Date(a.dateKey)
  );

  // inside each group, sort by time desc
  groupsArr.forEach((g) => {
    g.items.sort((a, b) => {
      if (!a.lastDate && !b.lastDate) return 0;
      if (!a.lastDate) return 1;
      if (!b.lastDate) return -1;
      return b.lastDate - a.lastDate;
    });
  });

  return groupsArr;
}

// ---------- Other helpers ----------

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = getUserLastDate({ lastTimestamp: ts });
  if (d && !isNaN(d.getTime())) {
    return d.toLocaleString();
  }
  return String(ts);
}

function flattenMessages(mapObj = {}) {
  return Object.entries(mapObj).map(([id, v]) => ({
    id,
    ...(v || {}),
  }));
}

// Parse a message's timestamp (tries several common fields)
function getMessageDate(msg = {}) {
  if (!msg) return null;
  const possible = [
    msg.timestamp,
    msg.time,
    msg.ts,
    msg.createdAt,
    msg.date,
    msg._seconds ? { _seconds: msg._seconds, _nanoseconds: msg._nanoseconds } : null,
  ].filter(Boolean);

  for (const p of possible) {
    // try using existing helper shape
    if (typeof p === "object" && p !== null && typeof p._seconds === "number") {
      const ms = p._seconds * 1000 + Math.round((p._nanoseconds || 0) / 1e6);
      return new Date(ms);
    }
    if (typeof p === "number") {
      const ms = p < 1e12 ? p * 1000 : p;
      return new Date(ms);
    }
    if (typeof p === "string") {
      const d = new Date(p);
      if (!isNaN(d.getTime())) return d;
    }
  }

  // fallback: if message has no recognizable timestamp, return null
  return null;
}

// ---------- Main component ----------

export default function AdminChatsPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [messages, setMessages] = useState({ sent: {}, reply: {} });

  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const timelineRef = useRef(null);

  async function apiFetch(path, options = {}) {
    const headers = options.headers || {};
    if (apiKeyValue) headers[API_KEY_HEADER] = apiKeyValue;
    headers["Content-Type"] = headers["Content-Type"] || "application/json";

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status} ${text}`);
    }
    return res.json();
  }

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await apiFetch("/projects");
        setProjects(data.projects || []);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setStatus("Failed to load projects: " + err.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedProject) return;

    (async () => {
      try {
        setLoading(true);
        const data = await apiFetch(`/${selectedProject}/chats`);
        setUsers(data.users || []);
        setSelectedUserId(null);
        setMessages({ sent: {}, reply: {} });
        setLoading(false);
        setStatus("");
      } catch (err) {
        setLoading(false);
        setStatus("Failed to load chats: " + err.message);
      }
    })();
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject || !selectedUserId) return;

    (async () => {
      try {
        setLoading(true);

        const data = await apiFetch(
          `/${selectedProject}/chats/${selectedUserId}/messages`
        );

        const raw = data.messages || {};
        const sentObj = {};
        const replyObj = {};

        Object.entries(raw).forEach(([id, v]) => {
          const val = typeof v === "string" ? { message: v } : v || {};
          const k = id.toLowerCase();
          if (k.includes("user")) sentObj[id] = val;
          else if (k.includes("reply")) replyObj[id] = val;
          else replyObj[id] = val;
        });

        setMessages({ sent: sentObj, reply: replyObj });

        setLoading(false);
        setStatus("");
      } catch (err) {
        setLoading(false);
        setStatus("Failed to load messages: " + err.message);
      }
    })();
  }, [selectedProject, selectedUserId]);

  async function handleSendReply() {
    if (!selectedProject || !selectedUserId) return;

    if (!replyText.trim()) {
      setStatus("Reply empty");
      return;
    }

    try {
      setLoading(true);

      await apiFetch(
        `/${selectedProject}/chats/${selectedUserId}/reply`,
        {
          method: "POST",
          body: JSON.stringify({ message: replyText }),
        }
      );

      setReplyText("");

      const data = await apiFetch(
        `/${selectedProject}/chats/${selectedUserId}/messages`
      );

      const raw = data.messages || {};
      const sentObj = {};
      const replyObj = {};
      Object.entries(raw).forEach(([id, v]) => {
        const val = typeof v === "string" ? { message: v } : v || {};
        const k = id.toLowerCase();
        if (k.includes("user")) sentObj[id] = val;
        else replyObj[id] = val;
      });

      setMessages({ sent: sentObj, reply: replyObj });

      setLoading(false);
    } catch (err) {
      setLoading(false);
      setStatus("Reply failed: " + err.message);
    }
  }

// Notify button handler — calls a full URL (set REACT_APP_NOTIFY_URL) or falls back to API_BASE + '/admin/sendMsg'
async function handleNotify() {
  if (!selectedUserId) {
    setStatus("No user selected for notify");
    return;
  }

  // Use a full URL from env if provided (can be totally different origin)
  const fullNotifyUrl = 'https://www.customizebychoice.com/pikchar_api/admin/sendMsg';

  try {
    setLoading(true);

    const headers = {
      "Content-Type": "application/json",
    };
    if (apiKeyValue) {
      headers[API_KEY_HEADER] = apiKeyValue;
    }

    const res = await fetch(fullNotifyUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({ id: selectedUserId, message: "new message!" }),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`${res.status} ${txt}`);
    }

    setStatus("Notify sent");
    setLoading(false);
  } catch (err) {
    setLoading(false);
    setStatus("Notify failed: " + err.message);
  }
}


  // Combine sent + reply into a single timeline array sorted ascending (old -> new)
  const sentList = flattenMessages(messages.sent);
  const replyList = flattenMessages(messages.reply);

  const mergedTimeline = React.useMemo(() => {
    const arr = [];

    sentList.forEach((m) => {
      arr.push({
        ...m,
        _role: "user",
        _date: getMessageDate(m) || getMessageDate({ timestamp: m.timestamp }),
      });
    });

    replyList.forEach((m) => {
      arr.push({
        ...m,
        _role: "admin",
        _date: getMessageDate(m) || getMessageDate({ timestamp: m.timestamp }),
      });
    });

    // if no dates, keep original order but push to bottom
    arr.sort((a, b) => {
      if (!a._date && !b._date) return 0;
      if (!a._date) return -1;
      if (!b._date) return 1;
      return a._date - b._date; // oldest first
    });

    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // scroll to bottom whenever timeline changes
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    // small timeout to allow rendering
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [mergedTimeline.length, selectedUserId]);

  // Build grouped/ordered chat list
  const userGroups = groupUsersByDate(users);

  // find selected user object (if needed)
  const currentUserObj = users.find((u) => u.id === selectedUserId);

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: "#fff",
        color: "#000",
      }}
    >
      {/* LEFT PANEL */}
      <div
        style={{
          width: 330,
          borderRight: "1px solid #ccc",
          padding: 12,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3 style={{ color: "#000" }}>Projects</h3>

        {projects.map((p) => (
          <button
            key={p}
            onClick={() => setSelectedProject(p)}
            style={{
              width: "100%",
              padding: 8,
              textAlign: "left",
              marginBottom: 6,
              borderRadius: 6,
              background: selectedProject === p ? "#dce6ff" : "#fff",
              border:
                selectedProject === p
                  ? "1px solid #5996ff"
                  : "1px solid #ccc",
              cursor: "pointer",
              color: "#000",
            }}
          >
            {p}
          </button>
        ))}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <h4 style={{ color: "#000" }}>Chats</h4>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              borderRadius: 6,
              paddingRight: 6,
            }}
          >
            {userGroups.length === 0 && (
              <div style={{ fontSize: 13, color: "#666" }}>No chats yet.</div>
            )}

            {userGroups.map((group) => (
              <div key={group.dateKey} style={{ marginBottom: 10 }}>
                {/* Date heading: Today / Yesterday / 24 Nov 2025 */}
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#444",
                    margin: "6px 2px",
                  }}
                >
                  {group.label}
                </div>

                {group.items.map((u) => {
                  const isSelected = selectedUserId === u.id;
                  const lastTimeStr =
                    u.lastDate &&
                    u.lastDate.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                  return (
                    <div
                      key={u.id}
                      onClick={() => setSelectedUserId(u.id)}
                      style={{
                        padding: 8,
                        marginBottom: 6,
                        borderRadius: 6,
                        background: isSelected ? "#dceeff" : "#fff",
                        border: isSelected
                          ? "1px solid #5996ff"
                          : "1px solid #ccc",
                        cursor: "pointer",
                        color: "#000",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {/* RED DOT: shown when lastSource is 'sent' */}
                        {u.lastSource === "sent" && (
                          <span
                            aria-hidden="true"
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              background: "#ff4d4d",
                              display: "inline-block",
                              marginRight: 8,
                            }}
                          />
                        )}

                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: "#000",
                              fontSize: 13,
                            }}
                          >
                            {u.name || u.id}
                          </div>
                          {u.email && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#555",
                                marginTop: 2,
                              }}
                            >
                              {u.email}
                            </div>
                          )}
                        </div>
                      </div>

                      {lastTimeStr && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#777",
                            marginLeft: 8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {lastTimeStr}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column" }}>
        <h2>Messages</h2>

        {status && (
          <div style={{ marginBottom: 8, fontSize: 12, color: "#c00" }}>
            {status}
          </div>
        )}

        {!selectedUserId && <div>Select a user</div>}

        {selectedUserId && (
          <>
            {/* Replaced static "User" label with the user's id as requested */}
            <h4>{selectedUserId}</h4>

            {/* TIMELINE */}
            <div
              ref={timelineRef}
              style={{
                flex: 1,
                background: "#fafafa",
                padding: 16,
                borderRadius: 8,
                maxHeight: "60vh",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {mergedTimeline.length === 0 && (
                <div style={{ color: "#666", fontSize: 13 }}>No messages</div>
              )}

              {mergedTimeline.map((m, idx) => {
                const isUser = m._role === "user";
                const bubbleStyle = {
                  maxWidth: "70%",
                  padding: "10px 12px",
                  borderRadius: 12,
                  borderTopLeftRadius: isUser ? 4 : 12,
                  borderTopRightRadius: isUser ? 12 : 4,
                  alignSelf: isUser ? "flex-start" : "flex-end",
                  background: isUser ? "#fff" : "#dceeff",
                  border: "1px solid #ddd",
                  color: "#000",
                  boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
                };

                const timeStr = (m._date && m._date instanceof Date && !isNaN(m._date.getTime()))
                  ? m._date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                  : formatTimestamp(m.timestamp || m.time || m.ts || m.createdAt);

                return (
                  <div
                    key={m.id || `${idx}-${m._role}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isUser ? "flex-start" : "flex-end",
                    }}
                  >
                    <div style={bubbleStyle}>
                      {/* message text */}
                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {m.message ?? m.text ?? String(m || "")}
                      </div>
                    </div>

                    <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                      <span style={{ marginRight: 6 }}>{isUser ? "User" : "Admin"}</span>
                      <span>{timeStr}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reply box */}
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              style={{
                width: "100%",
                marginTop: 12,
                padding: 10,
                border: "1px solid #777",
                borderRadius: 6,
                background: "#fff",
                color: "#000",
                resize: "vertical",
                minHeight: 60,
              }}
            />

            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
              <button
                onClick={handleSendReply}
                style={{
                  padding: "8px 12px",
                  background: "#2f6bff",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send"}
              </button>

              <button
                onClick={handleNotify}
                style={{
                  padding: "8px 12px",
                  background: "#ffb74d",
                  border: "none",
                  borderRadius: 6,
                  color: "#000",
                  cursor: "pointer",
                }}
                disabled={loading}
                title="Notify user via /admin/sendMsg"
              >
                {loading ? "Working..." : "Notify"}
              </button>

              <button
                onClick={() => setReplyText("")}
                style={{
                  padding: "8px 12px",
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: 6,
                  color: "#000",
                  cursor: "pointer",
                }}
                disabled={loading}
              >
                Clear
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
